import React, { useCallback, useEffect, useRef, useState } from "react";
import { beautyCancelSeriesApi, beautyListSeriesApi } from "../../../api/organizerBeautyApi";
import SpinLoading from "../../../components/spinLoading/SpinLoading";
import CourtSeriesKeptReport from "../../reservas/components/CourtSeriesKeptReport";
import { shortSeriesDate, weekdayName } from "../../reservas/components/courtSeriesModel";
import { hoyISO } from "./agendaLayout";
import { KEPT_LABELS, cancelacionMensaje, intervalHeading, proximasLabel, seriesErrorMessage } from "./turnoFijoModel";
import "../../events/EventForm.css";
import "../turnos-beauty.css";
import "../../primerUso/primerUso.css";
import "../../reservas/reservas-organizer.css";
import "../../reservas/components/CourtSeries.css";
import "./agenda.css";

/* ─────────────────────────────────────────
   TurnosFijosModal — series activas del salón (GET /series) con "Cancelar serie": cancela todas las fechas que
   quedan desde hoy (POST /series/:id/cancel). Las fechas ya cobradas o con ingreso las mantiene el back y se
   listan acá, porque siguen en pie. Mismas clases `csr-*` que la lista de turnos fijos de Canchas.
───────────────────────────────────────── */

const serviciosDe = (series) => (Array.isArray(series.services) ? series.services.join(" + ") : series.services || "");

const TurnosFijosModal = ({ salon, onClose, onCancelled }) => {
  const [estado, setEstado] = useState("loading"); // loading | error | ready
  const [errorCarga, setErrorCarga] = useState("");
  const [items, setItems] = useState([]);
  const [confirmando, setConfirmando] = useState(null);
  const [cancelando, setCancelando] = useState(null);
  const [errorCancelar, setErrorCancelar] = useState("");
  const [reporte, setReporte] = useState(null);
  const pedidoRef = useRef(0);

  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    const pedido = ++pedidoRef.current;
    if (!silencioso) setEstado("loading");
    setErrorCarga("");
    try {
      const res = await beautyListSeriesApi();
      if (pedido !== pedidoRef.current) return;
      const info = res?.data?.info;
      setItems(Array.isArray(info) ? info : []);
      setEstado("ready");
    } catch (e) {
      if (pedido !== pedidoRef.current) return;
      setErrorCarga(seriesErrorMessage(e, "No pudimos cargar los turnos fijos."));
      setEstado("error");
    }
  }, []);

  useEffect(() => {
    cargar();
    return () => {
      pedidoRef.current += 1;
    };
  }, [cargar]);

  const cerrar = () => {
    if (cancelando) return;
    onClose?.();
  };

  const cancelarSerie = async (series) => {
    setCancelando(series.id);
    setErrorCancelar("");
    try {
      const res = await beautyCancelSeriesApi(series.id, hoyISO());
      const resultado = res?.data?.info || {};
      setConfirmando(null);
      setReporte({ nombre: series.customer?.name, resultado });
      await onCancelled?.(resultado);
      await cargar({ silencioso: true });
    } catch (e) {
      setErrorCancelar(seriesErrorMessage(e, "No pudimos cancelar el turno fijo. Probá de nuevo."));
      // Already cancelled elsewhere: the list is stale.
      if (e?.response?.status === 409) await cargar({ silencioso: true });
    } finally {
      setCancelando(null);
    }
  };

  return (
    <div className="zr-overlay" onMouseDown={cerrar}>
      <div className="zr-modal zr-side-panel zr-srv-modal ba-modal" role="dialog" aria-modal="true" aria-label="Turnos fijos" onMouseDown={(e) => e.stopPropagation()}>
        <div className="zr-side-panel__head">
          <h3 className="zr-side-panel__title">Turnos fijos{salon?.name ? ` | ${salon.name}` : ""}</h3>
          <button type="button" className="zr-btn zr-btn--ghost zr-side-panel__close" onClick={cerrar} aria-label="Cerrar" disabled={Boolean(cancelando)}>
            ×
          </button>
        </div>
        <div className="pu-body">
          {errorCancelar && <div className="ef-alert" role="alert">{errorCancelar}</div>}
          {reporte && (
            <div className="ef-alert ef-alert--info" role="status">
              <div>
                {reporte.nombre ? `${reporte.nombre}: ` : ""}
                {cancelacionMensaje(reporte.resultado)}
              </div>
              <CourtSeriesKeptReport kept={reporte.resultado.kept} reasonLabels={KEPT_LABELS} />
            </div>
          )}

          {estado === "loading" ? (
            <SpinLoading />
          ) : estado === "error" ? (
            <div role="alert">
              <p className="csr-state">{errorCarga}</p>
              <button type="button" className="zr-btn zr-btn--soft" onClick={() => cargar()}>Reintentar</button>
            </div>
          ) : items.length === 0 ? (
            <p className="csr-state">No hay turnos fijos activos. Podés crear uno desde una reserva nueva con la opción "Turno fijo".</p>
          ) : (
            <ul className="csr-series ba-series" aria-label="Turnos fijos activos">
              {items.map((series) => (
                <li key={series.id} className="csr-series__item">
                  <div className="ba-series__info">
                    <span className="csr-series__title">{series.customer?.name || "Sin nombre"}</span>
                    <span className="csr-series__meta">
                      {series.professionalName || "Profesional"} · {weekdayName(series.weekday)} {series.slotStart}
                      {series.durationMinutes ? ` · ${series.durationMinutes} min` : ""}
                    </span>
                    {serviciosDe(series) && <span className="csr-series__meta">{serviciosDe(series)}</span>}
                    <span className="csr-series__meta">
                      {intervalHeading(series.intervalWeeks)} · Desde {shortSeriesDate(series.dateFrom)} hasta {shortSeriesDate(series.dateTo)}
                    </span>
                    <span className="csr-series__meta">{proximasLabel(series)}</span>
                  </div>
                  {confirmando === series.id ? (
                    <div className="csr-series__actions ba-series__confirm">
                      <span className="csr-series__meta">¿Cancelar todas las fechas que quedan desde hoy?</span>
                      <div className="ba-series__botones">
                        <button type="button" className="zr-btn zr-btn--ghost" disabled={cancelando === series.id} onClick={() => setConfirmando(null)}>
                          Volver
                        </button>
                        <button type="button" className="zr-btn zr-btn--dark" disabled={cancelando === series.id} onClick={() => cancelarSerie(series)}>
                          {cancelando === series.id ? "Cancelando..." : "Sí, cancelar serie"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="csr-series__actions">
                      <button
                        type="button"
                        className="zr-btn zr-btn--soft"
                        disabled={Boolean(cancelando)}
                        onClick={() => {
                          setErrorCancelar("");
                          setConfirmando(series.id);
                        }}
                      >
                        Cancelar serie
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default TurnosFijosModal;
